import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@newfront-insurance/core-ui/v2';

import { analyticsScopeKey, sortedAnalyticsScopes } from './analytics-access';
import type { NovaAnalyticsScope } from '../../../../queries/nova-analytics';

/** Organization labels group options; only discovered ORGANIZATION entries grant an org-wide option. */
export function NovaAnalyticsScopeSelect({
  scopes,
  selected,
  disabled,
  onChange,
}: {
  scopes: NovaAnalyticsScope[];
  selected?: NovaAnalyticsScope;
  disabled?: boolean;
  onChange: (scope: NovaAnalyticsScope) => void;
}) {
  const groups = new Map<string, NovaAnalyticsScope[]>();
  for (const scope of sortedAnalyticsScopes(scopes)) {
    const group = groups.get(scope.organization.id) ?? [];
    group.push(scope);
    groups.set(scope.organization.id, group);
  }
  return (
    <Select
      value={selected ? analyticsScopeKey(selected) : ''}
      disabled={disabled || scopes.length === 0}
      onValueChange={(key) => {
        const scope = scopes.find((item) => analyticsScopeKey(item) === key);
        if (scope) onChange(scope);
      }}
    >
      <SelectTrigger
        data-testid="nova-analytics-scope-select"
        aria-label="Organization or team"
        className="w-[280px] max-w-full text-sm"
      >
        <SelectValue placeholder="Choose an organization or team">
          {selected ? (
            <span className="flex min-w-0 items-center gap-1.5 truncate">
              <span className="text-steel-400 text-xs font-semibold">{selected.organization.name}</span>
              <span className="truncate">
                {selected.type === 'ORGANIZATION' ? 'Entire organization' : selected.name}
              </span>
            </span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="end" className="max-h-96 overflow-y-auto rounded-sm">
        {[...groups.entries()].map(([organizationId, group], index) => (
          <SelectGroup key={organizationId}>
            {index > 0 ? <SelectSeparator /> : null}
            <SelectLabel className="text-steel-400 px-6 py-2.5 text-xs">{group[0]!.organization.name}</SelectLabel>
            {group.map((scope) => (
              <SelectItem
                data-testid={`nova-analytics-scope-option-${analyticsScopeKey(scope)}`}
                key={analyticsScopeKey(scope)}
                value={analyticsScopeKey(scope)}
                aria-label={`${scope.organization.name}: ${scope.type === 'ORGANIZATION' ? 'Entire organization' : scope.name}`}
                className="py-2.5 text-sm"
              >
                <span className="sr-only">{scope.organization.name}: </span>
                {scope.type === 'ORGANIZATION' ? 'Entire organization' : scope.name}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
